import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Observable, of, Subscription } from 'rxjs';
import { concatMap, map, reduce } from 'rxjs/operators';
import { Tpl3plService } from '../../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../../services/globalsearchservice.service';

export interface SkuPushRow {
	stockid: string;
	status: 'pending' | 'sending' | 'success' | 'failed';
	message?: string;
	fullResult?: any;
}

export interface RawResponseModalData {
	orderno?: string;
	export_status?: string;
	context?: 'export' | 'inventory' | 'sku_push' | 'inbound' | 'open_orders' | 'shipments' | 'cancel';
	warehouse?: string;
	stockid?: string;
	content: any;
	onSent?: () => void;
}

@Component({
	selector: 'app-raw-response-modal',
	templateUrl: './raw-response-modal.component.html',
	styleUrls: ['./raw-response-modal.component.scss']
})
export class RawResponseModalComponent implements OnDestroy {
	copied = false;
	copiedRequest = false;
	copiedResponse = false;
	contentJson: string;
	requestPayloadJson: string | null = null;
	responseJson: string;
	isPreviewMode = false;
	sending = false;
	/** When true, inventory sync only inserts/updates SKUs not yet in tpl3pl_inventory (full PGS pull still runs). */
	inventorySyncNewSkusOnly = false;
	skuPushChunkSize = 50;
	skuPushRows: SkuPushRow[] = [];
	private _skuPushSearch = '';
	private skuPushSearchLoadTimeout: ReturnType<typeof setTimeout> | null = null;
	skuPushPageIndex = 0;
	skuPushPageSize = 25;
	/** Skip this many in-scope SKUs (stockid order) before loading — use after sending earlier batches (e.g. 1000 after first 1000). */
	skuPushLoadOffsetInput = 0;
	/** Offset of the first row currently in `skuPushRows` (set on each full reload). */
	skuPushListBaseOffset = 0;
	/** Server allows up to 5000 per request (see tpl3pl/sku/push_list). */
	readonly skuPushListFetchLimit = 5000;
	get skuPushSearch(): string { return this._skuPushSearch; }
	set skuPushSearch(v: string) {
		this._skuPushSearch = v ?? '';
		this.skuPushPageIndex = 0;
		if (this.data?.context !== 'sku_push' || this.data?.stockid) { return; }
		if ((this._skuPushSearch || '').trim() !== '') {
			this.skuPushLoadOffsetInput = 0;
		}
		if (this.skuPushSearchLoadTimeout != null) { clearTimeout(this.skuPushSearchLoadTimeout); }
		this.skuPushSearchLoadTimeout = setTimeout(() => {
			this.skuPushSearchLoadTimeout = null;
			this.loadSkuPushList();
		}, 400);
	}
	skuPushListLoading = false;
	skuPushListTotal: number | null = null;
	skuPushBatchSending = false;
	private skuPushSubs: Subscription[] = [];
	private shipmentPollSub: Subscription | null = null;
	get skuPushRowsFiltered(): SkuPushRow[] {
		return this.skuPushRows;
	}
	get skuPushRowsPaged(): SkuPushRow[] {
		const list = this.skuPushRowsFiltered;
		const start = this.skuPushPageIndex * this.skuPushPageSize;
		return list.slice(start, start + this.skuPushPageSize);
	}
	onSkuPushPage(event: PageEvent): void {
		this.skuPushPageIndex = event.pageIndex;
		this.skuPushPageSize = event.pageSize;
	}
	get skuPushPendingCount(): number {
		return this.skuPushRows.filter(r => r.status === 'pending').length;
	}
	get skuPushSucceededCount(): number {
		return this.skuPushRows.filter(r => r.status === 'success').length;
	}
	get skuPushFailedCount(): number {
		return this.skuPushRows.filter(r => r.status === 'failed').length;
	}
	get skuPushCanLoadMore(): boolean {
		if (this.skuPushListTotal == null) {
			return false;
		}
		return this.skuPushListBaseOffset + this.skuPushRows.length < this.skuPushListTotal;
	}
	get skuPushLoadedRangeLabel(): string {
		if (this.skuPushRows.length === 0) {
			return '';
		}
		const from = this.skuPushListBaseOffset + 1;
		const to = this.skuPushListBaseOffset + this.skuPushRows.length;
		if (this.skuPushListTotal != null) {
			return `${from}–${to} of ${this.skuPushListTotal}`;
		}
		return `${from}–${to}`;
	}
	get circuitOpen(): boolean {
		return !!(this.data?.content && (this.data.content as any).circuit_open);
	}

	private getDatesInRange(from: string, to: string): string[] {
		const dates: string[] = [];
		const start = new Date(from);
		const end = new Date(to);
		if (start > end) { return []; }
		const cursor = new Date(start);
		while (cursor <= end) {
			dates.push(cursor.getFullYear() + '-' + String(cursor.getMonth() + 1).padStart(2, '0') + '-' + String(cursor.getDate()).padStart(2, '0'));
			cursor.setDate(cursor.getDate() + 1);
		}
		return dates;
	}

	constructor(
		public dialogRef: MatDialogRef<RawResponseModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: RawResponseModalData,
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private dialog: MatDialog
	) {
		const content = data.content;
		if (data.context === 'cancel') {
			const hasPayload = content && typeof content === 'object' && content.request_payload != null;
			this.requestPayloadJson = hasPayload ? JSON.stringify(content.request_payload, null, 2) : null;
			const hasResponse = content && typeof content === 'object' && content.response !== undefined;
			this.isPreviewMode = !hasResponse;
			this.responseJson = hasResponse ? (typeof content.response === 'string' ? content.response : JSON.stringify(content.response, null, 2)) : '';
			this.contentJson = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
			return;
		}
		if (data.context === 'inbound') {
			const payload = content && typeof content === 'object' && content.request_payload != null ? content.request_payload : content;
			const body = payload && typeof payload === 'object' && payload.body != null ? payload.body : (typeof content?.request_payload?.body === 'object' ? content.request_payload.body : null);
			this.requestPayloadJson = body != null ? JSON.stringify(body, null, 2) : (content && typeof content === 'object' ? JSON.stringify(content, null, 2) : null);
			const hasResponse = content && typeof content === 'object' && content.response !== undefined;
			this.isPreviewMode = !hasResponse;
			this.responseJson = hasResponse ? (typeof content.response === 'string' ? content.response : JSON.stringify(content.response, null, 2)) : '';
			this.contentJson = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
			return;
		}
		if (data.context === 'inventory' || data.context === 'sku_push') {
			this.requestPayloadJson = content && typeof content === 'object' && content.request_payload != null
				? JSON.stringify(content.request_payload, null, 2) : null;
			const hasResponse = content && typeof content === 'object' && content.response !== undefined;
			this.isPreviewMode = !hasResponse;
			if (hasResponse) {
				const responsePart = content.response;
				this.responseJson = typeof responsePart === 'string' ? responsePart : JSON.stringify(responsePart, null, 2);
			} else {
				this.responseJson = '';
			}
			this.contentJson = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
			return;
		}
		if (data.context === 'shipments') {
			this.requestPayloadJson = content && typeof content === 'object' && content.request_payload != null
				? JSON.stringify(content.request_payload, null, 2) : null;
			const hasResponse = content && typeof content === 'object' && content.response !== undefined;
			this.isPreviewMode = !hasResponse;
			this.responseJson = hasResponse
				? (typeof content.response === 'string' ? content.response : JSON.stringify(content.response, null, 2))
				: '';
			this.contentJson = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
			return;
		}
		const hasRequest = content && typeof content === 'object' && content.request_payload != null;
		this.isPreviewMode = hasRequest && content.response === undefined && !(content as any).circuit_open;
		if (hasRequest) {
			this.requestPayloadJson = JSON.stringify(content.request_payload, null, 2);
			if (content.response !== undefined && content.response !== null) {
				const responsePart = content.response;
				this.responseJson = typeof responsePart === 'string' ? responsePart : JSON.stringify(responsePart, null, 2);
			} else {
				this.responseJson = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
			}
		} else {
			this.responseJson = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
		}
		this.contentJson = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
	}

	ngOnDestroy(): void {
		if (this.skuPushSearchLoadTimeout != null) {
			clearTimeout(this.skuPushSearchLoadTimeout);
			this.skuPushSearchLoadTimeout = null;
		}
		this.skuPushSubs.forEach(s => s.unsubscribe());
		this.skuPushSubs = [];
		if (this.shipmentPollSub) {
			this.shipmentPollSub.unsubscribe();
			this.shipmentPollSub = null;
		}
	}

	loadSkuPushList(): void {
		if (this.skuPushListLoading) { return; }
		this.skuPushListLoading = true;
		const search = this._skuPushSearch.trim() || undefined;
		const off = Math.max(0, Math.floor(Number(this.skuPushLoadOffsetInput) || 0));
		this.skuPushListBaseOffset = off;
		this.tpl3plService.getSkuPushList(off, this.skuPushListFetchLimit, search).subscribe({
			next: (res) => {
				this.skuPushListLoading = false;
				if (res.success) {
					this.skuPushRows = (res.stockids || []).map(stockid => ({ stockid, status: 'pending' as const }));
					this.skuPushListTotal = res.total ?? null;
					this.skuPushPageIndex = 0;
				}
			},
			error: () => {
				this.skuPushListLoading = false;
				this.globalSearchService.showNotification('Could not load SKU list', 'danger', 'bottom', 'right');
			}
		});
	}

	loadMoreSkuPushList(): void {
		if (this.skuPushListLoading || !this.skuPushCanLoadMore) { return; }
		this.skuPushListLoading = true;
		const offset = this.skuPushListBaseOffset + this.skuPushRows.length;
		const search = this._skuPushSearch.trim() || undefined;
		this.tpl3plService.getSkuPushList(offset, this.skuPushListFetchLimit, search).subscribe({
			next: (res) => {
				this.skuPushListLoading = false;
				if (res.success && res.stockids && res.stockids.length) {
					const newRows = res.stockids.map((stockid: string) => ({ stockid, status: 'pending' as const }));
					this.skuPushRows = [...this.skuPushRows, ...newRows];
				}
				if (res.success && res.total != null) {
					this.skuPushListTotal = res.total;
				}
			},
			error: () => {
				this.skuPushListLoading = false;
			}
		});
	}

	viewPayload(row: SkuPushRow): void {
		this.tpl3plService.getSkuPushPreview({ stockid: row.stockid }).subscribe({
			next: (res) => {
				if (res.success && res.request_payload) {
					this.dialog.open(RawResponseModalComponent, {
						width: '720px',
						maxWidth: '96vw',
						maxHeight: '90vh',
						data: { context: 'sku_push', stockid: row.stockid, content: { request_payload: res.request_payload } },
						panelClass: 'raw-response-dialog'
					});
				}
			}
		});
	}

	sendOneRow(row: SkuPushRow): void {
		if (row.status !== 'pending') { return; }
		row.status = 'sending';
		this.tpl3plService.pushOneSku(row.stockid).subscribe({
			next: (result: any) => {
				const ok = result && typeof result === 'object' && result.success;
				row.status = ok ? 'success' : 'failed';
				row.message = result?.message ?? (ok ? 'Pushed' : 'Failed');
				row.fullResult = result;
				if (this.data.onSent) { this.data.onSent(); }
			},
			error: (err) => {
				row.status = 'failed';
				row.message = err?.error?.message || err?.message || 'Request failed';
				row.fullResult = { success: false, message: row.message };
				if (this.data.onSent) { this.data.onSent(); }
			}
		});
	}

	sendBatchSkuPush(): void {
		const pending = this.skuPushRows.filter(r => r.status === 'pending').slice(0, this.skuPushChunkSize);
		if (pending.length === 0) {
			this.globalSearchService.showNotification('No pending SKUs to send', 'warning', 'bottom', 'right');
			return;
		}
		this.skuPushBatchSending = true;
		this.skuPushSubs = [];
		let completed = 0;
		const done = () => {
			completed++;
			if (completed === pending.length) {
				this.skuPushBatchSending = false;
				this.globalSearchService.showNotification(`Batch done: ${pending.length} sent`, 'success', 'bottom', 'right');
				if (this.data.onSent) { this.data.onSent(); }
			}
		};
		pending.forEach(row => {
			row.status = 'sending';
			const sub = this.tpl3plService.pushOneSku(row.stockid).subscribe({
				next: (result: any) => {
					const ok = result && typeof result === 'object' && result.success;
					row.status = ok ? 'success' : 'failed';
					row.message = result?.message ?? (ok ? 'Pushed' : 'Failed');
					row.fullResult = result;
					done();
				},
				error: (err) => {
					row.status = 'failed';
					row.message = err?.error?.message || err?.message || 'Request failed';
					row.fullResult = { success: false, message: row.message };
					done();
				}
			});
			this.skuPushSubs.push(sub);
		});
	}

	exportAllSkuPushResults(): void {
		const withResults = this.skuPushRows.filter(r => r.fullResult != null);
		if (withResults.length === 0) {
			this.globalSearchService.showNotification('No results to export', 'warning', 'bottom', 'right');
			return;
		}
		const payload = withResults.map(r => ({
			stockid: r.stockid,
			status: r.status,
			message: r.message,
			response: r.fullResult
		}));
		const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `sku-push-results-${new Date().toISOString().slice(0, 19).replace(/[T:-]/g, '')}.json`;
		a.click();
		URL.revokeObjectURL(url);
	}

	copyToClipboard(): void {
		this.contentJson && this.copyText(this.contentJson, 'all');
	}

	copyRequest(): void {
		this.requestPayloadJson && this.copyText(this.requestPayloadJson, 'request');
	}

	copyResponse(): void {
		this.responseJson && this.copyText(this.responseJson, 'response');
	}

	private copyText(text: string, which: 'all' | 'request' | 'response'): void {
		const done = () => {
			this.copied = which === 'all'; this.copiedRequest = which === 'request'; this.copiedResponse = which === 'response';
			setTimeout(() => { this.copied = false; this.copiedRequest = false; this.copiedResponse = false; }, 2000);
		};
		if (navigator.clipboard && navigator.clipboard.writeText) {
			navigator.clipboard.writeText(text).then(done).catch(() => { this.fallbackCopy(text); done(); });
		} else {
			this.fallbackCopy(text);
			done();
		}
	}

	private fallbackCopy(text: string): void {
		const ta = document.createElement('textarea');
		ta.value = text;
		ta.style.position = 'fixed';
		ta.style.opacity = '0';
		document.body.appendChild(ta);
		ta.select();
		try {
			document.execCommand('copy');
		} finally {
			document.body.removeChild(ta);
		}
	}

	sendExport(): void {
		if (this.sending || !this.data.orderno) { return; }
		this.sending = true;
		this.tpl3plService.exportOrder(this.data.orderno).subscribe({
			next: (result) => {
				this.sending = false;
				this.responseJson = JSON.stringify(result, null, 2);
				this.isPreviewMode = false;
				if (result.request_payload != null && !this.requestPayloadJson) {
					this.requestPayloadJson = JSON.stringify(result.request_payload, null, 2);
				}
				const content = this.data.content && typeof this.data.content === 'object' ? this.data.content : {};
				this.contentJson = JSON.stringify({ ...content, response: result }, null, 2);
				this.globalSearchService.showNotification(
					result.success ? 'Order exported' : 'Export failed: ' + (result.message || ''),
					result.success ? 'success' : 'danger',
					'bottom', 'right'
				);
				if (result.success && this.data.onSent) {
					this.data.onSent();
				}
			},
			error: (err) => {
				this.sending = false;
				const msg = err?.error?.message || err?.message || 'Export failed';
				this.responseJson = JSON.stringify({ success: false, message: msg, error: err?.error }, null, 2);
				this.isPreviewMode = false;
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			}
		});
	}

	sendInventorySync(): void {
		if (this.sending) { return; }
		this.sending = true;
		const warehouse = this.data.warehouse || undefined;
		this.tpl3plService.syncInventory(warehouse, { new_skus_only: this.inventorySyncNewSkusOnly }).subscribe({
			next: (result) => {
				this.sending = false;
				this.isPreviewMode = false;
				this.data.content = {
					...(this.data.content || {}),
					success: result?.success,
					message: result?.message,
					response: result?.response,
				};
				this.responseJson = JSON.stringify(result ?? {}, null, 2);
				this.contentJson = JSON.stringify(this.data.content, null, 2);
				this.globalSearchService.showNotification(
					result?.success ? 'Sync completed' : ('Sync failed: ' + (result?.message || '')),
					result?.success ? 'success' : 'danger',
					'bottom', 'right'
				);
				if (this.data.onSent) {
					this.data.onSent();
				}
			},
			error: (err) => {
				this.sending = false;
				const body = err?.error;
				const msg = (body && typeof body === 'object' && body.message) ? body.message : (err?.message || 'Sync failed');
				this.isPreviewMode = false;
				if (body && typeof body === 'object') {
					this.responseJson = JSON.stringify(body, null, 2);
					this.data.content = {
						...(this.data.content || {}),
						success: body.success === true,
						message: body.message ?? msg,
						response: body.response,
					};
				} else {
					this.responseJson = JSON.stringify({ success: false, message: msg, error: body }, null, 2);
					this.data.content = { ...(this.data.content || {}), success: false, message: msg };
				}
				this.contentJson = JSON.stringify(this.data.content, null, 2);
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			}
		});
	}

	sendSkuPush(): void {
		if (this.sending || !this.data.stockid) { return; }
		this.sending = true;
		this.tpl3plService.pushOneSku(this.data.stockid).subscribe({
			next: (result) => {
				this.sending = false;
				this.isPreviewMode = false;
				const raw = result?.response != null ? result.response : result;
				this.responseJson = typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
				this.data.content = { ...(this.data.content || {}), success: result?.success, response: result?.response };
				this.globalSearchService.showNotification(
					result?.success ? 'SKU pushed' : ('Push failed: ' + (result?.message || '')),
					result?.success ? 'success' : 'danger',
					'bottom', 'right'
				);
				if (this.data.onSent) {
					this.data.onSent();
				}
			},
			error: (err) => {
				this.sending = false;
				const msg = err?.error?.message || err?.message || 'Push failed';
				this.responseJson = JSON.stringify({ success: false, message: msg, error: err?.error }, null, 2);
				this.isPreviewMode = false;
				this.data.content = { ...(this.data.content || {}), success: false };
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			}
		});
	}

	getInboundPayloadBody(): any {
		if (!this.data?.content || typeof this.data.content !== 'object') { return null; }
		const rp = this.data.content.request_payload;
		if (rp && typeof rp === 'object' && rp.body != null) { return rp.body; }
		if (this.requestPayloadJson) {
			try { return JSON.parse(this.requestPayloadJson); } catch { return null; }
		}
		return null;
	}

	sendShipmentsPoll(): void {
		if (this.sending || this.data?.context !== 'shipments') { return; }
		const dr = this.data?.content?.request_payload?.date_range;
		const from = dr?.from;
		const to = dr?.to ?? from;
		if (!from) {
			this.globalSearchService.showNotification('No date range in request payload', 'warning', 'bottom', 'right');
			return;
		}
		const dates = this.getDatesInRange(from, to);
		if (dates.length === 0) {
			this.globalSearchService.showNotification('Invalid date range', 'warning', 'bottom', 'right');
			return;
		}
		this.sending = true;
		const pollOneRaw = (date: string): Observable<{ date: string; res: any }> =>
			this.tpl3plService.pollShipmentsRaw(date, date).pipe(map((res) => ({ date, res })));
		this.shipmentPollSub = of(...dates).pipe(
			concatMap((date) => pollOneRaw(date)),
			reduce(
				(acc: { date: string; request_payload: any; response_b2b: any; response_b2c: any; success: boolean }[], { date, res }: { date: string; res: any }) => {
					acc.push({
						date,
						request_payload: res?.request_payload ?? null,
						response_b2b: res?.response_b2b ?? null,
						response_b2c: res?.response_b2c ?? null,
						success: !!res?.success
					});
					return acc;
				},
				[] as { date: string; request_payload: any; response_b2b: any; response_b2c: any; success: boolean }[]
			)
		).subscribe({
			next: (byDate) => {
				this.shipmentPollSub = null;
				this.sending = false;
				const apiCalls = byDate.flatMap((d) => {
					const arr = Array.isArray(d.request_payload) ? d.request_payload : (d.request_payload != null ? [d.request_payload] : []);
					return arr.map((req: any) =>
						req && typeof req === 'object' ? { date: d.date, ...req } : { date: d.date, request: req }
					);
				});
				const request_payload = {
					date_range: { from: dates[0], to: dates[dates.length - 1], days: dates.length },
					api_calls: apiCalls
				};
				const response = byDate.length === 1
					? { b2b: byDate[0].response_b2b, b2c: byDate[0].response_b2c }
					: { byDate: byDate.map((d) => ({ date: d.date, b2b: d.response_b2b, b2c: d.response_b2c, success: d.success })) };
				this.data.content = { request_payload, response };
				this.requestPayloadJson = JSON.stringify(request_payload, null, 2);
				this.responseJson = typeof response === 'string' ? response : JSON.stringify(response, null, 2);
				this.contentJson = JSON.stringify(this.data.content, null, 2);
				this.isPreviewMode = false;
				this.globalSearchService.showNotification('Shipments poll completed', 'success', 'bottom', 'right');
				if (this.data.onSent) { this.data.onSent(); }
			},
			error: (err) => {
				this.shipmentPollSub = null;
				this.sending = false;
				const msg = err?.error?.message || err?.message || 'Poll failed';
				this.responseJson = JSON.stringify({ success: false, message: msg, error: err?.error }, null, 2);
				this.isPreviewMode = false;
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			}
		});
	}

	sendInbound(): void {
		if (this.sending) { return; }
		const body = this.getInboundPayloadBody();
		if (!body || !Array.isArray(body.inbound_item) || body.inbound_item.length === 0) {
			this.globalSearchService.showNotification('Invalid inbound payload: need body with inbound_item array', 'danger', 'bottom', 'right');
			return;
		}
		this.sending = true;
		this.tpl3plService.createInbound(body).subscribe({
			next: (result) => {
				this.sending = false;
				this.isPreviewMode = false;
				const raw = result?.response != null ? result.response : (result?.data != null ? result.data : result);
				this.responseJson = typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
				this.data.content = { ...(this.data.content || {}), success: result?.success, response: result?.response };
				this.globalSearchService.showNotification(
					result?.success ? 'Inbound sent' : ('Inbound failed: ' + (result?.message || '')),
					result?.success ? 'success' : 'danger',
					'bottom', 'right'
				);
				if (this.data.onSent) {
					this.data.onSent();
				}
			},
			error: (err) => {
				this.sending = false;
				const msg = err?.error?.message || err?.message || 'Inbound failed';
				this.responseJson = JSON.stringify({ success: false, message: msg, error: err?.error }, null, 2);
				this.isPreviewMode = false;
				this.data.content = { ...(this.data.content || {}), success: false };
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			}
		});
	}

	sendCancel(): void {
		if (this.sending || !this.data.orderno) { return; }
		this.sending = true;
		this.tpl3plService.cancelOrder(this.data.orderno).subscribe({
			next: (result) => {
				this.sending = false;
				this.isPreviewMode = false;
				if (result.request_payload != null && !this.requestPayloadJson) {
					this.requestPayloadJson = JSON.stringify(result.request_payload, null, 2);
				}
				const responsePart = result.response !== undefined ? result.response : result;
				this.responseJson = JSON.stringify(responsePart, null, 2);
				this.data.content = { request_payload: result.request_payload, response: responsePart, success: result.success, message: result.message };
				this.contentJson = JSON.stringify(this.data.content, null, 2);
				this.globalSearchService.showNotification(
					result.success ? 'Order cancelled at 3PL' : ('Cancel failed: ' + (result.message || '')),
					result.success ? 'success' : 'danger',
					'bottom', 'right'
				);
				if (result.success && this.data.onSent) {
					this.data.onSent();
				}
			},
			error: (err) => {
				this.sending = false;
				this.isPreviewMode = false;
				const msg = err?.error?.message || err?.message || 'Cancel request failed';
				const errBody = err?.error;
				if (errBody?.request_payload != null && !this.requestPayloadJson) {
					this.requestPayloadJson = JSON.stringify(errBody.request_payload, null, 2);
				}
				const responsePart = errBody?.response !== undefined ? errBody.response : errBody;
				this.responseJson = JSON.stringify(responsePart ?? { success: false, message: msg }, null, 2);
				this.data.content = { success: false, message: msg, response: responsePart };
				this.contentJson = JSON.stringify(this.data.content, null, 2);
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			}
		});
	}

	openRawForSkuRow(row: SkuPushRow): void {
		if (row.fullResult == null) { return; }
		this.dialog.open(RawResponseModalComponent, {
			width: '720px',
			maxWidth: '96vw',
			maxHeight: '90vh',
			data: {
				context: 'sku_push',
				stockid: row.stockid,
				content: row.fullResult,
				onSent: this.data.onSent
			},
			panelClass: 'raw-response-dialog'
		});
	}

	canSendBatch(): boolean {
		return !this.skuPushBatchSending && this.skuPushPendingCount > 0;
	}

	close(): void {
		this.dialogRef.close();
	}
}
